import type { CuratedWmsEntry } from "src/types/stencil/wmsCatalog";

const HAMBURG_BOUNDS: [[number, number], [number, number]] = [
  [9.7, 53.4],
  [10.4, 53.8],
];

const HAMBURG_ATTRIBUTION =
  "Freie und Hansestadt Hamburg, Landesbetrieb Geoinformation und Vermessung (LGV)";

const BERLIN_BOUNDS: [[number, number], [number, number]] = [
  [13.05, 52.33],
  [13.78, 52.69],
];
const BERLIN_ATTRIBUTION = "© GeoBasis-DE / GeoBasisdaten Berlin";

// Bremen and Bremerhaven are two non-adjacent cities in the Land Bremen.
// The cadastral WMS (alkishb_wms) covers both; planning data is city-specific.
const BREMEN_BOUNDS: [[number, number], [number, number]] = [
  [8.46, 53.0],
  [8.99, 53.24],
];
const BREMERHAVEN_BOUNDS: [[number, number], [number, number]] = [
  [8.51, 53.46],
  [8.72, 53.67],
];
const BREMEN_CADASTRE_ATTRIBUTION =
  "© GeoBasis-DE / Landesamt GeoInformation Bremen";
const BREMEN_PLANNING_ATTRIBUTION = "Freie Hansestadt Bremen";

// Hannover shares the Lower Saxony ALKIS WMS (LGLN) with Bremen — different
// layer name on the same provider, different bounding box.
const HANNOVER_BOUNDS: [[number, number], [number, number]] = [
  [9.59, 52.3],
  [9.92, 52.45],
];
const LOWER_SAXONY_CADASTRE_ATTRIBUTION = "© GeoBasis-DE / LGLN";

const FRANKFURT_BOUNDS: [[number, number], [number, number]] = [
  [8.47, 50.02],
  [8.81, 50.23],
];
const HESSEN_CADASTRE_ATTRIBUTION = "© GeoBasis-DE / HVBG";
const FRANKFURT_PLANNING_ATTRIBUTION = "Stadt Frankfurt am Main";

const MUENCHEN_BOUNDS: [[number, number], [number, number]] = [
  [11.36, 48.06],
  [11.72, 48.25],
];
const BAYERN_CADASTRE_ATTRIBUTION =
  "© Bayerische Vermessungsverwaltung";
const MUENCHEN_PLANNING_ATTRIBUTION = "Landeshauptstadt München";

const KOELN_BOUNDS: [[number, number], [number, number]] = [
  [6.77, 50.83],
  [7.16, 51.08],
];
const NRW_CADASTRE_ATTRIBUTION = "© Geobasis NRW";
const KOELN_PLANNING_ATTRIBUTION = "Stadt Köln";

const STUTTGART_BOUNDS: [[number, number], [number, number]] = [
  [9.04, 48.69],
  [9.32, 48.87],
];
const BW_CADASTRE_ATTRIBUTION =
  "© GeoBasis-DE / LGL Baden-Württemberg, CC BY 4.0";
const STUTTGART_PLANNING_ATTRIBUTION = "Landeshauptstadt Stuttgart";

export const WMS_CATALOG: CuratedWmsEntry[] = [
  {
    id: "hh-alkis-basiskarte",
    title: "Hamburg ALKIS Basiskarte (Flurstücke & Gebäude)",
    url: "https://geodienste.hamburg.de/HH_WMS_ALKIS_Basiskarte",
    layerName: "14,19",
    bounds: HAMBURG_BOUNDS,
    state: "Hamburg",
    category: "land-registry",
    attribution: HAMBURG_ATTRIBUTION,
  },
  {
    // Replaces HH_WMS_Cache_Rasterplan, which now only serves EPSG:25832.
    id: "hh-bplaene-geltungsbereiche",
    title: "Hamburg B-Pläne (Geltungsbereiche, festgestellt & im Verfahren)",
    url: "https://geodienste.hamburg.de/HH_WMS_Bebauungsplaene",
    layerName: "bplaene",
    bounds: HAMBURG_BOUNDS,
    state: "Hamburg",
    category: "planning-law",
    attribution: HAMBURG_ATTRIBUTION,
  },
  {
    id: "hh-bplaene-plis-vektor",
    title: "Hamburg B-Pläne (Vektordaten PLIS)",
    url: "https://geodienste.hamburg.de/HH_WMS_Cache_PLIS_Geodaten",
    layerName: "bp_objekte",
    bounds: HAMBURG_BOUNDS,
    state: "Hamburg",
    category: "planning-law",
    attribution: HAMBURG_ATTRIBUTION,
  },
  {
    id: "hh-flaechennutzungsplan",
    title: "Hamburg Flächennutzungsplan (FNP)",
    url: "https://geodienste.hamburg.de/HH_WMS_INSPIRE_Planned_Land_Use",
    layerName: "LU.PlannedLandUse",
    bounds: HAMBURG_BOUNDS,
    state: "Hamburg",
    category: "planning-law",
    attribution: HAMBURG_ATTRIBUTION,
  },
  {
    id: "hh-bodenrichtwerte-mfh",
    title: "Hamburg Bodenrichtwerte – Mehrfamilienhäuser (BRW)",
    url: "https://geodienste.hamburg.de/HH_WMS_UEKnormierteBodenrichtwerte",
    layerName: "brw_uek_mfh",
    bounds: HAMBURG_BOUNDS,
    state: "Hamburg",
    category: "brw",
    attribution: HAMBURG_ATTRIBUTION,
  },

  // ---- Berlin ----
  {
    id: "bln-alkis",
    title: "Berlin ALKIS (Flurstücke & Gebäude)",
    url: "https://gdi.berlin.de/services/wms/alkis",
    // Only renders at scale ≤ 1:2550 (very close zoom) — provider restriction.
    layerName: "a_alkis_raster",
    bounds: BERLIN_BOUNDS,
    state: "Berlin",
    category: "land-registry",
    attribution: BERLIN_ATTRIBUTION,
  },
  {
    id: "bln-baunutzungsplan",
    title: "Berlin Baunutzungsplan (West)",
    url: "https://gdi.berlin.de/services/wms/bnp",
    layerName: "bnp",
    bounds: BERLIN_BOUNDS,
    state: "Berlin",
    category: "planning-law",
    attribution: BERLIN_ATTRIBUTION,
  },
  {
    id: "bln-flaechennutzungsplan",
    title: "Berlin Flächennutzungsplan (FNP 2015)",
    url: "https://gdi.berlin.de/services/wms/plu_fnp2015",
    layerName: "LU.ZoningElement,LU.SupplementaryRegulation,LU.SpatialPlan",
    bounds: BERLIN_BOUNDS,
    state: "Berlin",
    category: "planning-law",
    attribution: BERLIN_ATTRIBUTION,
  },
  {
    id: "bln-bplaene-festgesetzt",
    title: "Berlin Bebauungspläne (festgesetzt)",
    url: "https://gdi.berlin.de/services/wms/bplan",
    layerName: "b_bp_fs",
    bounds: BERLIN_BOUNDS,
    state: "Berlin",
    category: "planning-law",
    attribution: BERLIN_ATTRIBUTION,
  },
  {
    id: "bln-bodenrichtwerte",
    title: "Berlin Bodenrichtwerte (2026)",
    url: "https://gdi.berlin.de/services/wms/brw2026",
    layerName: "brw2026",
    bounds: BERLIN_BOUNDS,
    state: "Berlin",
    category: "brw",
    attribution: BERLIN_ATTRIBUTION,
  },

  // ---- Bremen (city) ----
  {
    id: "hb-alkis",
    title: "Bremen ALKIS (Flurstücke & Gebäude)",
    url: "https://opendata.lgln.niedersachsen.de/doorman/noauth/alkishb_wms",
    // Only renders at scale ≤ 1:5000 (close zoom) — provider restriction.
    layerName: "ALKIS",
    bounds: BREMEN_BOUNDS,
    state: "Bremen",
    category: "land-registry",
    attribution: BREMEN_CADASTRE_ATTRIBUTION,
  },
  {
    id: "hb-flaechennutzungsplan",
    title: "Bremen Flächennutzungsplan",
    url: "https://baugis.dataport.de/arcgis/services/OPEN/FP_WMS/MapServer/WMSServer",
    // Group layers have no requestable name; request all FNP area polygons.
    layerName:
      "1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24",
    bounds: BREMEN_BOUNDS,
    state: "Bremen",
    category: "planning-law",
    attribution: BREMEN_PLANNING_ATTRIBUTION,
  },

  // ---- Bremerhaven ----
  {
    id: "bhv-alkis",
    title: "Bremerhaven ALKIS (Flurstücke & Gebäude)",
    url: "https://opendata.lgln.niedersachsen.de/doorman/noauth/alkishb_wms",
    layerName: "ALKIS",
    bounds: BREMERHAVEN_BOUNDS,
    state: "Bremerhaven",
    category: "land-registry",
    attribution: BREMEN_CADASTRE_ATTRIBUTION,
  },
  {
    id: "bhv-flaechennutzungsplan",
    title: "Bremerhaven Flächennutzungsplan (2006)",
    url: "https://geodienste.bremen.de/FNP_BHV_2006",
    layerName: "Flaechennutzungsplan_BHV_2006",
    bounds: BREMERHAVEN_BOUNDS,
    state: "Bremerhaven",
    category: "planning-law",
    attribution: BREMEN_PLANNING_ATTRIBUTION,
  },
  {
    id: "bhv-bplaene",
    title: "Bremerhaven Bebauungspläne",
    url: "https://geodienste.bremen.de/wms_bebauungsplaene_bhv",
    layerName: "bebauungsplaene_bhv",
    bounds: BREMERHAVEN_BOUNDS,
    state: "Bremerhaven",
    category: "planning-law",
    attribution: BREMEN_PLANNING_ATTRIBUTION,
  },

  // ---- Hannover ----
  // B-Plan + FNP go via a MapGuide viewer that doesn't expose WMS — both
  // blocked pending an alternative endpoint or the MapGuide API.
  {
    id: "han-alkis",
    title: "Hannover ALKIS (Flurstücke & Gebäude)",
    url: "https://opendata.lgln.niedersachsen.de/doorman/noauth/alkis_wms",
    // Provider restricts to close zoom (typical for ALKIS).
    layerName: "ALKIS",
    bounds: HANNOVER_BOUNDS,
    state: "Hannover",
    category: "land-registry",
    attribution: LOWER_SAXONY_CADASTRE_ATTRIBUTION,
  },

  // ---- Frankfurt am Main ----
  // B-Plan is on a planas.frankfurt.de viewer URL — blocked pending the
  // sibling WMS (the Seveso layer below is on the same domain, so one likely
  // exists for B-Plan too).
  {
    id: "ffm-alkis",
    title: "Frankfurt ALKIS (Liegenschaftskarte)",
    url: "https://www.gds-srv.hessen.de/cgi-bin/lika-services/ogc-free-maps.ows",
    layerName: "he_alk",
    bounds: FRANKFURT_BOUNDS,
    state: "Frankfurt am Main",
    category: "land-registry",
    attribution: HESSEN_CADASTRE_ATTRIBUTION,
  },
  {
    id: "ffm-bodenrichtwerte",
    title: "Frankfurt Bodenrichtwerte (2026)",
    url: "https://geowebdienste.frankfurt.de/BRW2026",
    // Root layer has no name — request both children (zones + values).
    layerName: "Bodenrichtwertzone,Bodenrichtwert",
    bounds: FRANKFURT_BOUNDS,
    state: "Frankfurt am Main",
    category: "brw",
    attribution: FRANKFURT_PLANNING_ATTRIBUTION,
  },
  {
    id: "ffm-seveso",
    title: "Frankfurt Seveso-Abstandszonen",
    url: "https://planas.frankfurt.de/wms/seveso_abstandszonen",
    layerName: "seveso_abstandszonen",
    bounds: FRANKFURT_BOUNDS,
    state: "Frankfurt am Main",
    category: "planning-law",
    attribution: FRANKFURT_PLANNING_ATTRIBUTION,
  },

  // ---- München ----
  {
    id: "mun-alkis",
    title: "München ALKIS-Parzellarkarte",
    url: "https://geoservices.bayern.de/od/wms/alkis/v1/parzellarkarte",
    layerName: "by_alkis_parzellarkarte_farbe",
    bounds: MUENCHEN_BOUNDS,
    state: "München",
    category: "land-registry",
    attribution: BAYERN_CADASTRE_ATTRIBUTION,
  },
  {
    id: "mun-flaechennutzungsplan",
    title: "München Flächennutzungsplan (FNP)",
    url: "https://geoportal.muenchen.de/geoserver/plan/g_fnp/ows",
    layerName: "g_fnp",
    bounds: MUENCHEN_BOUNDS,
    state: "München",
    category: "planning-law",
    attribution: MUENCHEN_PLANNING_ATTRIBUTION,
  },
  {
    id: "mun-bplaene",
    title: "München Bebauungspläne",
    url: "https://geoportal.muenchen.de/geoserver/gsm_wfs/vagrund_baug_umgriff_opendata/ows",
    layerName: "vagrund_baug_umgriff_opendata",
    bounds: MUENCHEN_BOUNDS,
    state: "München",
    category: "planning-law",
    attribution: MUENCHEN_PLANNING_ATTRIBUTION,
  },

  // ---- Köln ----
  {
    id: "koe-alkis",
    title: "Köln ALKIS (Flurstücke & Gebäude)",
    url: "https://www.wms.nrw.de/geobasis/wms_nw_alkis",
    layerName: "adv_alkis_flurstuecke,adv_alkis_gebaeude",
    bounds: KOELN_BOUNDS,
    state: "Köln",
    category: "land-registry",
    attribution: NRW_CADASTRE_ATTRIBUTION,
  },
  {
    id: "koe-flaechennutzungsplan",
    title: "Köln Flächennutzungsplan",
    url: "https://geoportal.stadt-koeln.de/wss/service/fnp_wms/guest",
    layerName: "aktueller_fnp",
    bounds: KOELN_BOUNDS,
    state: "Köln",
    category: "planning-law",
    attribution: KOELN_PLANNING_ATTRIBUTION,
  },
  {
    id: "koe-bplaene",
    title: "Köln Bebauungspläne (rechtsverbindlich)",
    url: "https://geoportal.stadt-koeln.de/wss/service/bplan_bauportal_wms/guest",
    layerName: "geltungsbereiche",
    bounds: KOELN_BOUNDS,
    state: "Köln",
    category: "planning-law",
    attribution: KOELN_PLANNING_ATTRIBUTION,
  },
  {
    id: "koe-bodenrichtwerte",
    title: "Köln Bodenrichtwerte",
    url: "https://www.wms.nrw.de/boris/wms_nw_brw",
    // Root layer has no name. Request the polygon + label layers for every
    // BRW category (residential, agricultural, commercial, etc.).
    layerName: "2,3,5,6,8,9,11,12,14,15,17,18,20,21",
    bounds: KOELN_BOUNDS,
    state: "Köln",
    category: "brw",
    attribution: NRW_CADASTRE_ATTRIBUTION,
  },

  // ---- Stuttgart ----
  {
    id: "stg-flaechennutzungsplan",
    title: "Stuttgart Flächennutzungsplan (FNP)",
    url: "https://geoserver.stuttgart.de/geoserver/ows",
    // The "EPSG25832" in the layer name is the source data's native CRS;
    // GeoServer reprojects on request so EPSG:3857 works fine.
    layerName: "GEOLINE_FLEX:A61_FNP_ohne_Nr_EPSG25832_TIFF",
    bounds: STUTTGART_BOUNDS,
    state: "Stuttgart",
    category: "planning-law",
    attribution: STUTTGART_PLANNING_ATTRIBUTION,
  },
  {
    id: "stg-alkis",
    title: "Stuttgart ALKIS Basis",
    url: "https://owsproxy.lgl-bw.de/owsproxy/ows/WMS_LGL-BW_ALKIS_Basis_Vertrieb",
    layerName: "nora:ALKIS_Basis_Vertrieb",
    bounds: STUTTGART_BOUNDS,
    state: "Stuttgart",
    category: "land-registry",
    attribution: BW_CADASTRE_ATTRIBUTION,
  },
  {
    id: "stg-bplaene",
    title: "Stuttgart Bebauungspläne",
    url: "https://geoserver.stuttgart.de/geoserver/ows",
    layerName: "GEOLINE_FLEX:A61_BPLAN_EPSG25832",
    bounds: STUTTGART_BOUNDS,
    state: "Stuttgart",
    category: "planning-law",
    attribution: STUTTGART_PLANNING_ATTRIBUTION,
  },
  {
    id: "stg-bodenrichtwerte",
    title: "Stuttgart Bodenrichtwerte",
    url: "https://geoserver.stuttgart.de/geoserver/ows",
    layerName: "GEOLINE_FLEX:WMS_BRWK",
    bounds: STUTTGART_BOUNDS,
    state: "Stuttgart",
    category: "brw",
    attribution: STUTTGART_PLANNING_ATTRIBUTION,
  },
];
